// ---------------------------------------------------------------
// Orders (online purchases + phone-initiated sales). An order is the bridge
// between "a customer bought something" and "devices are provisioned + shipped".
// Stored locally for now (same as customers); moves to the accounts backend
// when that's built. Live card charging happens through Stripe Checkout — the
// order records the intended amount so the admin queue always shows what was
// paid and how many devices were ordered.
// ---------------------------------------------------------------

const KEY = 'dd-orders';
const get = () => { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; } };
const save = (a) => localStorage.setItem(KEY, JSON.stringify(a));

// What customers can buy. Prices are in USD. A "device" line ships hardware; a
// "plan" line is the recurring membership. deviceCount drives how many trackers
// the admin needs to provision + ship for an order.
export const PRODUCTS = [
  { id: 'dev-obd', name: 'TagAlong OBD Tracker', kind: 'device', price: 149, deviceCount: 1, blurb: 'Plug-in OBD-II tracker. Self-install in seconds.' },
  { id: 'dev-hw', name: 'TagAlong Hardwired Tracker', kind: 'device', price: 179, deviceCount: 1, blurb: 'Hidden hardwired tracker. Professional install.' },
  { id: 'plan-track', name: 'Track — yearly membership', kind: 'plan', price: 99, deviceCount: 0, plan: 'track', blurb: 'Live tracking + in-app alerts, billed yearly.' },
  { id: 'plan-talk', name: 'Talk — monthly membership', kind: 'plan', price: 15, deviceCount: 0, plan: 'talk', blurb: 'Two-way text & call with the car, billed monthly.' },
];
export const productById = (id) => PRODUCTS.find((p) => p.id === id) || null;

// Order lifecycle. The admin drives an order left→right in the Orders tab.
export const ORDER_STATUSES = [
  { id: 'new', label: 'New', color: '#f97316' },          // just purchased — needs attention
  { id: 'provisioning', label: 'Provisioning', color: '#0284c7' }, // assigning devices to the account
  { id: 'shipped', label: 'Shipped', color: '#7c3aed' },  // hardware on the way
  { id: 'active', label: 'Active', color: '#16a34a' },    // customer up and running
];
export const statusById = (id) => ORDER_STATUSES.find((s) => s.id === id) || ORDER_STATUSES[0];

// items: [{ productId, qty }]. Total + device count are derived so the admin
// queue can show "$X paid · N devices" at a glance.
export const orderTotal = (order) => (order.items || []).reduce((sum, it) => {
  const p = productById(it.productId); return sum + (p ? p.price * (it.qty || 1) : 0);
}, 0);
export const orderDeviceCount = (order) => (order.items || []).reduce((n, it) => {
  const p = productById(it.productId); return n + (p ? (p.deviceCount || 0) * (it.qty || 1) : 0);
}, 0);

export const getOrders = () => get().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
export const getOrder = (id) => get().find((o) => o.id === id);
// how many orders still need the admin's attention (drives the sidebar badge)
export const newOrdersCount = () => get().filter((o) => o.status === 'new').length;

export const blankOrder = () => ({
  customerId: '',        // linked customer record (customerStore)
  account: '',           // their 4-digit account number
  customerName: '', email: '', phone: '',
  address: '', city: '', state: '', zip: '', // ship-to
  items: [],             // [{ productId, qty }]
  amountPaid: 0,         // what actually cleared (set once Stripe confirms)
  paid: false,           // true once payment clears
  paymentRef: '',        // Stripe session / charge id when live
  source: 'online',      // 'online' (self-serve) | 'phone' (admin-created)
  status: 'new',
  deviceIds: [],         // devices provisioned to fulfill this order
  tracking: '',          // shipping tracking number
  notes: '',
  seen: false,           // admin has opened it (clears the "new" highlight)
});

export function addOrder(o) {
  const a = get();
  const rec = { id: `O${Date.now()}`, createdAt: new Date().toISOString(), ...blankOrder(), ...o };
  rec.amountPaid = rec.amountPaid || orderTotal(rec); // record intended amount if not yet charged
  a.push(rec); save(a); return rec;
}
export function updateOrder(id, patch) { save(get().map((o) => (o.id === id ? { ...o, ...patch } : o))); }
export function removeOrder(id) { save(get().filter((o) => o.id !== id)); }
